
import { SpotifyArtistProfile, MusicalCompatibilityFactors } from '@/types';

/**
 * Calculate musical compatibility between two artists
 */
export function calculateMusicalCompatibility(
  artist1: SpotifyArtistProfile, 
  artist2: SpotifyArtistProfile
): MusicalCompatibilityFactors | null {
  // Only calculate if both have audio features
  if (!artist1.audioFeatures || !artist2.audioFeatures) {
    return null;
  }
  
  // Calculate tempo alignment (closer tempos = higher score)
  const tempoDiff = Math.abs(artist1.audioFeatures.averageTempo - artist2.audioFeatures.averageTempo);
  const tempoAlignment = Math.max(0, 100 - (tempoDiff * 2)); // Max 100, less difference = higher score
  
  // Calculate mood alignment based on mood profiles
  const moodDiffs = [
    Math.abs(artist1.audioFeatures.moodProfile.happy - artist2.audioFeatures.moodProfile.happy),
    Math.abs(artist1.audioFeatures.moodProfile.energetic - artist2.audioFeatures.moodProfile.energetic),
    Math.abs(artist1.audioFeatures.moodProfile.chill - artist2.audioFeatures.moodProfile.chill),
    Math.abs(artist1.audioFeatures.moodProfile.melancholic - artist2.audioFeatures.moodProfile.melancholic)
  ];
  const avgMoodDiff = moodDiffs.reduce((sum, diff) => sum + diff, 0) / moodDiffs.length;
  const moodAlignment = Math.max(0, 100 - (avgMoodDiff * 100)); // Convert to percentage
  
  // Calculate audience overlap - this is an estimation based on genre overlap and popularity
  const genreOverlap = artist1.audioFeatures.topGenres.filter(genre => 
    artist2.audioFeatures.topGenres.includes(genre)
  ).length / Math.max(artist1.audioFeatures.topGenres.length, artist2.audioFeatures.topGenres.length) * 100;
  
  const popDiff = Math.abs((artist1.popularity || 50) - (artist2.popularity || 50));
  const audienceOverlap = (genreOverlap * 0.7) + Math.max(0, 100 - popDiff) * 0.3;
  
  // Calculate artistic complementarity - higher when artists have some similarities but also some differences
  // (We want some overlap but also some uniqueness for a good collaboration)
  const energyComplementarity = 100 - Math.abs(artist1.audioFeatures.averageEnergy - artist2.audioFeatures.averageEnergy) * 50;
  const danceabilityComplementarity = 100 - Math.abs(artist1.audioFeatures.averageDanceability - artist2.audioFeatures.averageDanceability) * 50;
  const artisticComplementarity = (energyComplementarity + danceabilityComplementarity) / 2;
  
  return {
    tempoAlignment,
    moodAlignment,
    audienceOverlap,
    artisticComplementarity
  };
}

/**
 * Generate reasons why two artists would be a good match
 */
export function generateCompatibilityReasons(
  artist1: SpotifyArtistProfile, 
  artist2: SpotifyArtistProfile,
  userProfile1: { name: string; role: string },
  userProfile2: { name: string; role: string },
  musicalCompatibility: MusicalCompatibilityFactors
): string[] {
  const reasons: string[] = [];
  
  // Genre overlap
  const sharedGenres = artist1.audioFeatures?.topGenres.filter(genre => 
    artist2.audioFeatures?.topGenres.includes(genre)
  ) || [];
  
  if (sharedGenres.length > 0) {
    reasons.push(`You both work within similar genres: ${sharedGenres.join(', ')}`);
  }
  
  // Role complementarity
  if (userProfile1.role !== userProfile2.role) {
    reasons.push(`${userProfile2.name}'s skills as a ${userProfile2.role} complement your work as a ${userProfile1.role}`);
  }
  
  // Tempo & mood compatibility
  if (musicalCompatibility.tempoAlignment > 75) {
    reasons.push('Your musical tempos align well, suggesting a natural rhythmic compatibility');
  }
  
  if (musicalCompatibility.moodAlignment > 75) {
    reasons.push('You create music with similar emotional qualities and mood profiles');
  } else if (musicalCompatibility.moodAlignment > 40) {
    reasons.push('Your contrasting emotional approaches could create an interesting musical tension');
  }
  
  // Artistic complementarity
  if (musicalCompatibility.artisticComplementarity > 70) {
    reasons.push('Your production styles would complement each other well');
  }
  
  // Audience overlap
  if (musicalCompatibility.audienceOverlap > 70) {
    reasons.push('You likely share similar audience demographics');
  } else if (musicalCompatibility.audienceOverlap > 40) {
    reasons.push('Collaborating could help both of you reach new audience segments');
  }
  
  // If we don't have enough reasons, add a generic one
  if (reasons.length < 3) {
    reasons.push('Based on your musical profiles, you could create something unique together');
  }
  
  return reasons;
}
